import { ADMIN_ROLES, FEATURES } from "../constants/enums";
import { AdminPermissions, GetVariables } from "../models/app";
import useFeature from "./useFeature";
import useAdminRole from "./useAdminRole";
import { findFeature } from "../helpers/utils";

const usePermissions = () => {
  const { featuresAllListing } = useFeature("features", "feature");
  const { adminRolesGet } = useAdminRole("adminRoles", "adminRole");

  const adminPermissions: AdminPermissions = {
    canEditSettings: false,
    hasAllConcepts: false,
    canSeeDashboard: false,
    canSeeCustomers: false,
    canSeeReceipts: false,
    canSeeInvoices: false,
    canSeeZatcaInvoices: false,
    canSeeInvokers: false,
    canPerformInvoicesOperations: false,
    canReturnReceipt: false,
    canResubmitReceipt: false,
    concepts: [],
  };

  const getAdminPermissions = async (props: {
    account: any;
    allFeatures: any[];
    userGroup: any;
  }): Promise<AdminPermissions> => {
    const { account, allFeatures, userGroup } = props;

    if (userGroup && userGroup.length > 0) {
      let userRoles: any[] = [];

      // All Roles
      for (let group of userGroup) {
        userRoles = userRoles.concat(group.roles);
      }

      console.log('[usePermissions] User roles to check:', userRoles);
      console.log('[usePermissions] All features available:', allFeatures?.map((f: any) => ({ id: f.id, name: f.name })));
      console.log('[usePermissions] Account features IDs:', account?.features);
      
      for (let role of userRoles) {
        const adminRolesParams: GetVariables = {
          id: role,
        };
        const userRole = await adminRolesGet(adminRolesParams.id);
        if (userRole === null || userRole === undefined) {
          console.warn('[usePermissions] Role not found:', role);
          continue;
        }

        console.log('[usePermissions] Checking role ID:', role, 'Role name:', userRole.name);
        
        // Debug: Check if this is the return receipt role
        if (role === '49c921b7-566d-4567-a0a9-1f6b0e5ebfa1') {
          console.log('[usePermissions] Found return receipt role ID, checking name:', userRole.name);
          console.log('[usePermissions] Expected name:', ADMIN_ROLES.RETURN_RECEIPT);
          console.log('[usePermissions] Match?', userRole.name === ADMIN_ROLES.RETURN_RECEIPT);
        }
        
        // Check account feature with group role
        switch (userRole.name) {
          case ADMIN_ROLES.SETTINGS:
            adminPermissions.canEditSettings = true;
            break;
          case ADMIN_ROLES.DASHBOARD:
            adminPermissions.canSeeDashboard = true;
            break;
          case ADMIN_ROLES.CUSTOMERS:
            adminPermissions.canSeeCustomers = true;
            break;
          case ADMIN_ROLES.RECEIPTS:
            if (account && account.features) {
              let feature: any = await findFeature(
                allFeatures,
                account.features,
                FEATURES.RECEIPTS
              );

              if (feature) adminPermissions.canSeeReceipts = true;
            }

            break;

          case ADMIN_ROLES.INVOICES:
            adminPermissions.canSeeInvoices = true;
            break;

          case ADMIN_ROLES.ZATCA_INVOICES:
            if (account && account.features) {
              let feature: any = await findFeature(
                allFeatures,
                account.features,
                FEATURES.ZATCA_INVOICES
              ); // FEATURES.ZATCA_CREDIT_NOTES

              if (feature) adminPermissions.canSeeZatcaInvoices = true;
            }

            break;

          case ADMIN_ROLES.PERFORM_INVOICES_OPERATIONS:
            adminPermissions.canPerformInvoicesOperations = true;
            break;
          case ADMIN_ROLES.INVOKERS:
            adminPermissions.canSeeInvokers = true;
            break;
          case ADMIN_ROLES.ALL_CONCEPTS:
            adminPermissions.hasAllConcepts = true;
            break;
          case ADMIN_ROLES.RETURN_RECEIPT:
            adminPermissions.canReturnReceipt = true;
            break;
          case ADMIN_ROLES.RESUBMIT_RECEIPTS:
            adminPermissions.canResubmitReceipt = true;
            break;
          default:
            console.log('[usePermissions] Role name does not match any ADMIN_ROLES case:', userRole.name, '(Role ID:', role, ')');
            console.log('[usePermissions] Expected role names:', Object.values(ADMIN_ROLES));
            break;
        }
      }
    }

    return adminPermissions;
  };

  return { getAdminPermissions };
};

export default usePermissions;
